import styled from 'styled-components'
import PropTypes from 'prop-types'
import { t } from 'ttag'
import { useMutation } from 'react-query'
import { useContext, useMemo, useState } from 'react'

import { Globals } from '@h2/globals'
import { api, errors } from '@h2/api'
import { Card } from '@h2/common/components/card'
import { features } from '@h2/components/media'
import { PG, SLB, NAVIANCEBLUE } from '@h2/components/colors'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { Select } from '@h2/components/inputs/select'
import { Alert } from '@h2/components/display/alert'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Loader } from '@h2/components/loading/loader'

import { Create } from '../highlights/create'
import { Submit } from '../submit'
import { Controls } from '../controls'

  const Form = styled.form`
    display: flex;
    flex-direction: column;
    height: 100%;
 
    > h2 {
      font-size: 24px;
      line-height: 28px;
    }
 
    > fieldset {
      flex-direction: column;
    }
 
    @media (${features.minWidth.laptop}) {
      > h2 {
        font-size: 30px;
        line-height: 34px;
      }
 
      > fieldset {
        flex-direction: row;
      }
    }
  `
const MissingDoB = ({ inDialog, properties, renderPanel }) => {
    const { isNavianceUser } = useContext(Globals)
    const code = useMemo(() => properties.authorizationCode, [properties.authorizationCode])
    const [errorMsg, setErrorMsg] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [selectedMonth, setSelectedMonth] = useState('')
    const [selectedDate, setSelectedDate] = useState('')
    const [selectedYear, setSelectedYear] = useState('')
    const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), [])
    const years = useMemo(() => {
        const currentYear = new Date().getFullYear()

        return Array.from({ length: 100 }, (_, i) => currentYear - i)
    }, [])
    const getDaysInMonth = (month, year) => {
        if (month === undefined || year === undefined) {
            return 0;
        }

        return new Date(year, month, 0).getDate();
    };
    const days = useMemo(
        () => Array(getDaysInMonth(selectedMonth, selectedYear)).fill().map((_, index) => index + 1),
        [selectedMonth, selectedYear]
    )
    const padWithZero = (number) => number.toString().padStart(2, '0');
    const { error, handleOnSubmit } = useForm({})
    const { mutate: updateDobMutation, isLoading: isUpdatingDoB } = useMutation(
        async () => {
            const dobString = `${padWithZero(selectedMonth)}-${padWithZero(selectedDate)}-${selectedYear}`

            return await api.fetch(`/auth/dob`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    code,
                    dateOfBirth: dobString,
                }),
            })
        },
          {
             // Triggered right before the mutation function is called
              onMutate: () => setIsLoading(true),

              onSuccess: ({ membership }) => {
                  setIsLoading(false)

                  const { removed } = membership

                  if (removed) {
                      return
                  }

                  renderPanel('membership')
              },

             onError: err => {
                  setIsLoading(false)
                  setErrorMsg(errors.getMessage(err))
              }
          }
      )
      const isSubmitDisabled = useMemo(() => {
          return isUpdatingDoB || !selectedDate || !selectedMonth || !selectedYear
      }, [isUpdatingDoB, selectedDate, selectedMonth, selectedYear])


    // If you want a loader
    if (isLoading) {
      return <Loader />
    }

    return (

          <Card inDialog={inDialog}>
              <Card.Highlight
                  color={isNavianceUser ? NAVIANCEBLUE : properties.isNavigator ? SLB : PG}>
                  <Create
                      type={
                          isNavianceUser
                              ? 'navianceUser'
                              : properties.isNavigator
                                  ? 'navigator'
                                  : 'explorer'
                      }
                  />
              </Card.Highlight>
              <Card.Content>
                  <Form
                      id="missing-dob"
                      onSubmit={handleOnSubmit((form) => {
                        updateDobMutation(form)
                      })}
                      noValidate
                  >
                      <Alert
                          margin="0 0 15px 0"
                          type="error"
                          isVisible={Boolean(errorMsg)}
                          message={errorMsg}
                          onClose={() => setErrorMsg('')}
                      />


                              <Text size="medium" margin="0">
                                  {t`Please provide your birthdate`}
                              </Text>
                              <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                  <Field
                                      isRequired
                                      width="30%"
                                      label={t`Month`}
                                      desc={error.name}
                                      status={error.name ? 'error' : 'default'}>
                                      <Select
                                          items={months}
                                          value={selectedMonth}
                                          onChange={(event) => {
                                              setSelectedMonth(padWithZero(event.target.value))
                                          }}
                                          placeholder="Select Month"
                                      />
                                  </Field>
                                  <Field
                                      isRequired
                                      width="30%"
                                      label={t`Day`}
                                      desc={error.name}
                                      status={error.name ? 'error' : 'default'}>
                                      <Select
                                          items={days}
                                          value={selectedDate}
                                          onChange={(event) => {
                                              setSelectedDate(padWithZero(event.target.value))
                                          }}
                                          placeholder="Select Date"
                                          isDisabled={!selectedMonth}
                                      />
                                  </Field>
                                  <Field
                                      isRequired
                                      width="30%"
                                      label={t`Year`}
                                      desc={error.name}
                                      status={error.name ? 'error' : 'default'}>
                                      <Select
                                          items={years}
                                          value={selectedYear}
                                          onChange={event => {
                                              setSelectedYear(event.target.value)
                                          }}
                                          placeholder="Select Year"
                                      />
                                  </Field>
                              </div>


                      <Controls>
                          <Submit
                              text={t`Update birthdate`}
                              isLoading={isUpdatingDoB}
                              isDisabled={isSubmitDisabled}
                          />
                      </Controls>
                  </Form>
              </Card.Content>
          </Card>
    )
}

MissingDoB.propTypes = {
    inDialog: PropTypes.bool,
    properties: PropTypes.object.isRequired,
    renderPanel: PropTypes.func.isRequired
}

export {MissingDoB}
